import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Plus, RefreshCw, Search, LayoutGrid, List as ListIcon, MoreHorizontal, Pencil, Trash2,
  Phone, Mail, MapPin, Building2, Star, ShieldAlert, Heart, Users, Stethoscope, Scale, HelpCircle,
} from "lucide-react";
import PersonAvatar from "@/components/PersonAvatar";
import { useAuth } from "@/context/AuthContext";
import ImportantPersonDialog from "@/pages/client-sections/ImportantPersonDialog";

// ---- Category mapping ------------------------------------------------------

const HEALTH_ROLES = new Set([
  "GP", "Nurse", "Consultant", "Physiotherapist", "Occupational Therapist",
  "Speech Therapist", "Pharmacist", "Psychiatrist", "Psychologist", "Dentist",
]);
const SOCIAL_CARE_ROLES = new Set([
  "Social Worker", "Care Coordinator", "Support Worker", "Local Authority",
]);
const ADVOCATE_ROLES = new Set(["Advocate", "Solicitor", "IMCA"]);
const FAMILY_ROLES = new Set(["Family", "Friend", "Next of Kin"]);

function categoryOf(r) {
  if (r.is_emergency_contact || r.role === "Emergency Contact") return "emergency";
  if (HEALTH_ROLES.has(r.role)) return "health";
  if (SOCIAL_CARE_ROLES.has(r.role)) return "social";
  if (ADVOCATE_ROLES.has(r.role)) return "advocate";
  if (r.is_next_of_kin || FAMILY_ROLES.has(r.role)) return "family";
  return "other";
}

const CATEGORY_FILTERS = [
  { key: "all", label: "All", icon: Users },
  { key: "family", label: "Family", icon: Heart },
  { key: "health", label: "Health Professionals", icon: Stethoscope },
  { key: "social", label: "Social Care Professionals", icon: Users },
  { key: "advocate", label: "Advocates", icon: Scale },
  { key: "emergency", label: "Emergency Contacts", icon: ShieldAlert },
  { key: "other", label: "Other", icon: HelpCircle },
];

// ---- Utilities -------------------------------------------------------------

function displayName(r) {
  const parts = [r.title, r.first_name, r.last_name].filter(Boolean).map((s) => String(s).trim()).filter(Boolean);
  if (parts.length) return parts.join(" ");
  return (r.name || "").trim() || "Unnamed contact";
}

function humanDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(iso); }
}

function Badge({ tone = "sky", children, testid }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    red: "bg-red-50 text-red-700 border-red-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${tones[tone]}`}>
      {children}
    </span>
  );
}

function badgesFor(r) {
  const out = [];
  if (r.is_primary_contact) out.push({ tone: "sky", label: "Primary", icon: Star, testid: `ip-badge-primary-${r.id}` });
  if (r.is_next_of_kin) out.push({ tone: "indigo", label: "Next of Kin", icon: Heart, testid: `ip-badge-nok-${r.id}` });
  if (r.is_emergency_contact) out.push({ tone: "red", label: "Emergency", icon: ShieldAlert, testid: `ip-badge-emergency-${r.id}` });
  const cat = categoryOf(r);
  if (cat === "health") out.push({ tone: "emerald", label: "Health", icon: Stethoscope });
  else if (cat === "social") out.push({ tone: "amber", label: "Social Care", icon: Users });
  else if (cat === "advocate") out.push({ tone: "violet", label: "Advocate", icon: Scale });
  else if (cat === "family" && !r.is_next_of_kin) out.push({ tone: "sky", label: "Family", icon: Heart });
  return out;
}

// ---- Card ------------------------------------------------------------------

function ImportantPersonCard({ row, onOpen, onEdit, onDelete, canDelete }) {
  const badges = badgesFor(row);
  const name = displayName(row);
  return (
    <article
      className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden"
      data-testid={`ip-card-${row.id}`}
    >
      <div className="p-4 flex-1">
        <div className="flex items-start gap-3">
          <PersonAvatar person={row} name={name} size="lg" authed testid={`ip-avatar-${row.id}`} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-slate-900 truncate" data-testid={`ip-name-${row.id}`}>{name}</h3>
                <p className="text-xs text-slate-600 truncate">
                  {row.role || "—"}
                  {row.organization && <> · <span className="text-slate-500">{row.organization}</span></>}
                </p>
                {row.relationship && (
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">Relationship: {row.relationship}</p>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid={`ip-menu-${row.id}`} aria-label="Actions">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white">
                  <DropdownMenuItem onClick={() => onOpen(row)} data-testid={`ip-menu-view-${row.id}`}>View Details</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onEdit(row)} data-testid={`ip-menu-edit-${row.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                  </DropdownMenuItem>
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onDelete(row)} className="text-red-600" data-testid={`ip-menu-delete-${row.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {badges.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {badges.map((b, i) => {
                  const Ico = b.icon;
                  return (
                    <Badge key={i} tone={b.tone} testid={b.testid}>
                      <Ico className="w-3 h-3" /> {b.label}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 space-y-1.5 text-[12px] text-slate-700">
          {row.phone && (
            <div className="flex items-center gap-2 truncate"><Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" /> <span className="truncate">{row.phone}</span></div>
          )}
          {row.email && (
            <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" /> <span className="truncate">{row.email}</span></div>
          )}
          {row.preferred_contact_method && row.preferred_contact_method !== "any" && (
            <div className="text-[11px] text-slate-500">
              Prefers: <span className="font-medium capitalize">{row.preferred_contact_method}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-3 border-t border-slate-100 bg-slate-50/50">
        <Button
          size="sm"
          onClick={() => onOpen(row)}
          className="w-full h-8 bg-sky-700 hover:bg-sky-800 text-white"
          data-testid={`ip-details-${row.id}`}
        >View Details</Button>
      </div>
    </article>
  );
}

// ---- List row --------------------------------------------------------------

function ImportantPersonListRow({ row, onOpen, onEdit, onDelete, canDelete }) {
  const name = displayName(row);
  const badges = badgesFor(row);
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onOpen(row)} data-testid={`ip-list-row-${row.id}`}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <PersonAvatar person={row} name={name} size="sm" authed />
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">{name}</div>
            <div className="text-[11px] text-slate-500 truncate">{row.role}{row.organization ? ` · ${row.organization}` : ""}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-slate-600 truncate">{row.relationship || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2 text-slate-600 truncate">{row.phone || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2 text-slate-600 truncate">{row.email || <span className="text-slate-400">—</span>}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {badges.slice(0, 3).map((b, i) => {
            const Ico = b.icon;
            return (<Badge key={i} tone={b.tone}><Ico className="w-3 h-3" /> {b.label}</Badge>);
          })}
        </div>
      </td>
      <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onEdit(row)} className="p-1 text-slate-500 hover:text-sky-700" aria-label="Edit"><Pencil className="w-3.5 h-3.5" /></button>
        {canDelete && (
          <button onClick={() => onDelete(row)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </td>
    </tr>
  );
}

// ---- Detail drawer ---------------------------------------------------------

function DetailDrawer({ row, open, onOpenChange, onEdit }) {
  if (!row) return null;
  const name = displayName(row);
  const badges = badgesFor(row);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="ip-drawer">
        <SheetHeader>
          <SheetTitle className="sr-only">Contact details</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center text-center gap-2 pt-2">
          <PersonAvatar person={row} name={name} size="2xl" authed testid="ip-drawer-avatar" />
          <h2 className="font-heading text-lg font-semibold text-slate-900 mt-2" data-testid="ip-drawer-name">{name}</h2>
          <p className="text-sm text-slate-600">{row.role}{row.organization ? ` · ${row.organization}` : ""}</p>
          {row.relationship && <p className="text-xs text-slate-500">Relationship: {row.relationship}</p>}
          {badges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1 justify-center">
              {badges.map((b, i) => {
                const Ico = b.icon;
                return (<Badge key={i} tone={b.tone}><Ico className="w-3 h-3" /> {b.label}</Badge>);
              })}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3 text-sm">
          {row.phone && <DetailLine icon={Phone} label="Primary phone" value={row.phone} />}
          {row.secondary_phone && <DetailLine icon={Phone} label="Secondary phone" value={row.secondary_phone} />}
          {row.email && <DetailLine icon={Mail} label="Email" value={row.email} />}
          {row.organization && <DetailLine icon={Building2} label="Organisation" value={row.organization} />}
          {row.address && <DetailLine icon={MapPin} label="Address" value={row.address} multiline />}
          {row.preferred_contact_method && row.preferred_contact_method !== "any" && (
            <DetailLine icon={Star} label="Preferred contact" value={row.preferred_contact_method[0].toUpperCase() + row.preferred_contact_method.slice(1)} />
          )}
          {row.notes && (
            <div className="border-t border-slate-200 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Notes</div>
              <p className="text-slate-700 whitespace-pre-wrap" data-testid="ip-drawer-notes">{row.notes}</p>
            </div>
          )}
          <div className="border-t border-slate-200 pt-3 text-[11px] text-slate-500">
            Last updated <span className="tabular-nums">{humanDate(row.updated_at || row.created_at)}</span>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <Button className="flex-1 bg-sky-700 hover:bg-sky-800" onClick={() => onEdit(row)} data-testid="ip-drawer-edit">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="ip-drawer-close">Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailLine({ icon: Ico, label, value, multiline }) {
  return (
    <div className="flex items-start gap-3">
      <Ico className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className={`text-slate-800 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{value}</div>
      </div>
    </div>
  );
}

// ---- Main view -------------------------------------------------------------

export default function ImportantPeopleView({ clientId, clientName }) {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "manager";
  const canManage = user?.role === "admin" || user?.role === "manager";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [view, setView] = useState("cards");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/sections/important-people`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (category !== "all" && categoryOf(r) !== category) return false;
      if (q) {
        const hay = [displayName(r), r.name, r.role, r.relationship, r.organization, r.phone, r.email]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, query, category]);

  const counts = useMemo(() => {
    const c = { all: rows.length, family: 0, health: 0, social: 0, advocate: 0, emergency: 0, other: 0 };
    rows.forEach((r) => { c[categoryOf(r)] += 1; });
    return c;
  }, [rows]);

  const submit = async (values) => {
    try {
      if (editing?.id) {
        await api.put(`/clients/${clientId}/sections/important-people/${editing.id}`, { data: values });
        toast.success("Contact updated");
      } else {
        await api.post(`/clients/${clientId}/sections/important-people`, { data: values });
        toast.success("Contact created");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete ${displayName(row)}?`)) return;
    try {
      await api.delete(`/clients/${clientId}/sections/important-people/${row.id}`);
      toast.success("Contact deleted");
      if (drawerRow?.id === row.id) setDrawerRow(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDrawer = (row) => setDrawerRow(row);
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); setDrawerRow(null); };
  const onPhotoUpdated = (patch) => {
    if (!editing) return;
    const next = { ...editing, ...patch };
    setEditing(next);
    setRows((rs) => rs.map((r) => (r.id === next.id ? { ...r, ...patch } : r)));
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{clientName || "Service User"}</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="ip-title">Important People</h2>
            <p className="text-sm text-slate-500 mt-0.5" data-testid="ip-helper">
              Family members, professionals and other people who are important to {clientName ? `${clientName.split(" ")[0]}’s` : "this person’s"} care and support.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="ip-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-9 bg-sky-700 hover:bg-sky-800" data-testid="ip-new">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Contact
              </Button>
            )}
          </div>
        </div>

        {/* Filters row */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search Important People..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-9 pl-8 text-sm bg-white"
              data-testid="ip-search"
            />
          </div>

          <div className="sm:ml-auto inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" role="group" aria-label="View toggle">
            <button
              onClick={() => setView("cards")}
              className={`h-9 px-2.5 text-xs font-medium inline-flex items-center gap-1 ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              aria-pressed={view === "cards"}
              data-testid="ip-view-cards"
            ><LayoutGrid className="w-3.5 h-3.5" /> Card View</button>
            <button
              onClick={() => setView("list")}
              className={`h-9 px-2.5 text-xs font-medium inline-flex items-center gap-1 border-l border-slate-200 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              aria-pressed={view === "list"}
              data-testid="ip-view-list"
            ><ListIcon className="w-3.5 h-3.5" /> List View</button>
          </div>
        </div>

        {/* Category chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {CATEGORY_FILTERS.map((c) => {
            const Ico = c.icon;
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                data-testid={`ip-filter-${c.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full border transition-colors ${
                  active
                    ? "bg-sky-700 text-white border-sky-700"
                    : "bg-white text-slate-600 border-slate-200 hover:border-sky-400"
                }`}
              >
                <Ico className="w-3 h-3" /> {c.label}
                <span className={`ml-1 tabular-nums ${active ? "text-sky-100" : "text-slate-400"}`}>{counts[c.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="ip-loading">Loading contacts…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center bg-white border border-slate-200 rounded-lg" data-testid="ip-empty">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-600">
              {rows.length === 0 ? "No Important People yet. Add a family member or professional to get started." : "No contacts match your filters."}
            </div>
          </div>
        ) : view === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ip-grid">
            {filtered.map((r) => (
              <ImportantPersonCard
                key={r.id}
                row={r}
                onOpen={openDrawer}
                onEdit={openEdit}
                onDelete={doDelete}
                canDelete={canDelete}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ip-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Relationship</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Tags</th>
                  <th className="w-24 px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <ImportantPersonListRow
                    key={r.id}
                    row={r}
                    onOpen={openDrawer}
                    onEdit={openEdit}
                    onDelete={doDelete}
                    canDelete={canDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ImportantPersonDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        record={editing}
        onSubmit={submit}
        onPhotoUpdated={onPhotoUpdated}
      />

      <DetailDrawer
        row={drawerRow}
        open={!!drawerRow}
        onOpenChange={(v) => { if (!v) setDrawerRow(null); }}
        onEdit={openEdit}
      />
    </div>
  );
}
